import axios from 'axios';
import { authService } from './authService';

// Создание экземпляра axios
const apiClient = axios.create({
  headers: {
    'Content-Type': 'application/json'
  }
});

// Перехватчик для установки базового URL перед каждым запросом
apiClient.interceptors.request.use(
  (config) => {
    // Устанавливаем базовый URL из authService
    config.baseURL = authService.getCurrentServer() + '/api';
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Перехватчик для добавления токена авторизации
apiClient.interceptors.request.use(
  (config) => {
    const token = authService.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Перехватчик для обработки ошибок авторизации
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        await authService.refreshAccessToken();
        const token = authService.getToken();
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        // Перенаправляем на страницу логина
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }
    
    return Promise.reject(error);
  }
);

// Функция для расчёта премии
export const calculatePremium = async (polunioType, version, data) => {
  try {
    const payload = {
      version: {
        type: polunioType,
        number: version
      },
      ...data
    };

    const response = await apiClient.post('/rate/4/rating', payload);
    return { success: true, data: response.data };
  } catch (error) {
    if (error.response) {
      return { 
        success: false, 
        error: error.response.data?.message || 'Server error occurred' 
      };
    } else if (error.request) {
      return { 
        success: false, 
        error: 'No response from server. Please check if the API is running.' 
      };
    } else {
      return { 
        success: false, 
        error: error.message || 'An unexpected error occurred' 
      };
    }
  }
};

// Функция для получения последней доступной версии для типа полиса
export const getLatestVersion = async (polunioType) => {
  try {
    const response = await apiClient.get('/rate/4/latest-version', {
      params: { type: polunioType }
    });
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || 'Failed to load latest version'
    };
  }
};

// Функция для получения настроек (options JSON) — роут POST, version в теле
export const getSettings = async (polunioType, version) => {
  try {
    const response = await apiClient.post('/rate/4/settings', {
      version: {
        type: polunioType,
        number: version
      }
    });
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || 'Failed to load settings'
    };
  }
};

// Функция для скачивания XLSX настроек
export const downloadSettingsXlsx = async (polunioType, version) => {
  try {
    const response = await apiClient.post('/rate/4/settings/xlsx', {
      version: {
        type: polunioType,
        number: version
      }
    }, {
      responseType: 'blob'
    });

    // Создаем blob URL и скачиваем файл
    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;

    // Получаем имя файла из заголовка Content-Disposition, если есть
    const contentDisposition = response.headers['content-disposition'];
    let filename = `settings-type${polunioType}-v${version}.xlsx`;
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '');
      }
    }

    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || 'Failed to download settings'
    };
  }
};

export default apiClient;